import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { alertsAPI, notificationsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import FundingRow from '../components/funding/FundingRow';
import AlertButton from '../components/funding/AlertButton';
import { STATUS_COLORS, STATUS_LABELS } from '../utils/constants';

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const key = status.toLowerCase().replace(/ /g, '_');
  const bg = STATUS_COLORS[key];
  const label = STATUS_LABELS[key] || status;
  if (!bg) return <span className="font-semibold">{label}</span>;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-white"
      style={{ backgroundColor: bg }}
    >
      {label}
    </span>
  );
};

const AlertsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [schemeAlerts, setSchemeAlerts] = useState([]);
  const [collectionAlerts, setCollectionAlerts] = useState([]);
  const [funderAlerts, setFunderAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null); // set after data loads
  const [showReadAlerts, setShowReadAlerts] = useState(false);

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);
  const unreadCount = unreadNotifications.length;
  const totalWatching = schemeAlerts.length + collectionAlerts.length + funderAlerts.length;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [notifs, schemes, collections, funders] = await Promise.all([
        notificationsAPI.list(),
        alertsAPI.listSchemeAlerts(),
        alertsAPI.listCollectionAlerts(),
        alertsAPI.listFunderAlerts(),
      ]);
      setNotifications(notifs);
      setSchemeAlerts(schemes);
      setCollectionAlerts(collections);
      setFunderAlerts(funders);
    } catch (err) {
      console.error('Failed to fetch alerts data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Set default tab after data loads
  useEffect(() => {
    if (!loading && tab === null) {
      setTab(unreadCount > 0 ? 'notifications' : 'alerts');
    }
  }, [loading, tab, unreadCount]);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      window.dispatchEvent(new Event('alerts-read'));
    } catch (err) {
      console.error('Failed to mark alert read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new Event('alerts-read'));
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const renderNotificationItem = (n) => (
    <div
      key={n.id}
      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
        n.read ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'
      }`}
    >
      {/* Bell icon */}
      <div className={`flex-shrink-0 mt-0.5 ${n.read ? 'text-gray-400' : 'text-[#615DEC]'}`}>
        <svg className="h-5 w-5" fill={n.read ? 'none' : 'currentColor'} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${n.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
          {/* Render message with status badge inline */}
          {n.data?.new_status_display ? (
            <>
              {n.scheme_name || n.collection_name || 'Item'} is now{' '}
              <StatusBadge status={n.data.new_status_display} />
            </>
          ) : (
            n.message
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span className="text-xs text-gray-400">
            {new Date(n.created_at).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
            {n.event_type_display}
          </span>
          {n.organisation_name && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              {n.organisation_name}
            </span>
          )}
          {n.collection_name && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
              {n.collection_name}
            </span>
          )}
          {n.scheme_slug && (
            <Link
              to={`/funding/${n.scheme_slug}`}
              className="text-xs text-[#615DEC] hover:underline"
            >
              View opportunity
            </Link>
          )}
        </div>
      </div>

      {/* Mark read */}
      {!n.read && (
        <button
          onClick={() => handleMarkRead(n.id)}
          className="flex-shrink-0 text-xs text-gray-400 hover:text-gray-600"
          title="Mark as read"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
    </div>
  );

  // Tab buttons — order depends on whether there are unread alerts
  const alertsTab = (
    <button
      key="notifications"
      onClick={() => setTab('notifications')}
      className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        tab === 'notifications'
          ? 'border-[#615DEC] text-[#615DEC]'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      Alerts
      {unreadCount > 0 && (
        <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-[#615DEC] text-white">
          {unreadCount}
        </span>
      )}
    </button>
  );

  const watchingTab = (
    <button
      key="alerts"
      onClick={() => setTab('alerts')}
      className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        tab === 'alerts'
          ? 'border-[#615DEC] text-[#615DEC]'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      Watching
      {totalWatching > 0 && (
        <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-gray-200 text-gray-600">
          {totalWatching}
        </span>
      )}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
        <p className="text-sm text-gray-500 mt-1">
          Set alerts on opportunities, collections, or funders to get alerted when their status changes.
        </p>
      </div>

      {/* Tabs — Alerts first when there are unread, Watching first otherwise */}
      <div className="flex border-b border-gray-200 mb-6 mt-4">
        {unreadCount > 0
          ? <>{alertsTab}{watchingTab}</>
          : <>{watchingTab}{alertsTab}</>
        }
      </div>

      {/* Alerts (notifications) tab */}
      {tab === 'notifications' && (
        <div>
          {unreadCount > 0 && (
            <div className="flex justify-end mb-4">
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-[#615DEC] hover:underline"
              >
                Mark all as read
              </button>
            </div>
          )}

          {notifications.length === 0 ? (
            <div className="text-center py-16">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="mt-4 text-gray-500">No alerts yet</p>
              <p className="text-sm text-gray-400 mt-1">
                See all alerts from opportunities, collections, or funders here when they appear
              </p>
            </div>
          ) : (
            <div>
              {/* Unread alerts */}
              {unreadNotifications.length > 0 && (
                <div className="space-y-2">
                  {unreadNotifications.map(renderNotificationItem)}
                </div>
              )}

              {/* Read alerts — collapsible */}
              {readNotifications.length > 0 && (
                <div className={unreadNotifications.length > 0 ? 'mt-6' : ''}>
                  <button
                    onClick={() => setShowReadAlerts(!showReadAlerts)}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-3"
                  >
                    <svg
                      className={`h-4 w-4 transition-transform ${showReadAlerts ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                    {showReadAlerts ? 'Hide' : 'Show'} read alerts ({readNotifications.length})
                  </button>
                  {showReadAlerts && (
                    <div className="space-y-2">
                      {readNotifications.map(renderNotificationItem)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Watching tab */}
      {tab === 'alerts' && (
        <div>
          {totalWatching === 0 ? (
            <div className="text-center py-16">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="mt-4 text-gray-500">You're not watching anything yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Click the bell icon on any opportunity, collection, or funder to start watching it and receive alerts
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Scheme alerts */}
              {schemeAlerts.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Opportunities ({schemeAlerts.length})
                  </h2>
                  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                    {schemeAlerts.map(alert => (
                      <FundingRow
                        key={alert.id}
                        funding={alert.scheme_detail}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Collection alerts */}
              {collectionAlerts.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Collections ({collectionAlerts.length})
                  </h2>
                  <div className="space-y-2">
                    {collectionAlerts.map(alert => (
                      <div
                        key={alert.id}
                        className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {alert.collection_name}
                        </span>
                        <AlertButton
                          collectionId={alert.fund_collection}
                          initialActive={true}
                          onToggle={(active) => { if (!active) setCollectionAlerts(prev => prev.filter(a => a.id !== alert.id)); }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Funder alerts */}
              {funderAlerts.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-3">
                    Funders ({funderAlerts.length})
                  </h2>
                  <div className="space-y-2">
                    {funderAlerts.map(alert => (
                      <div
                        key={alert.id}
                        className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {alert.organisation_name}
                        </span>
                        <AlertButton
                          funderId={alert.organisation}
                          initialActive={true}
                          onToggle={(active) => { if (!active) setFunderAlerts(prev => prev.filter(a => a.id !== alert.id)); }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
